import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, NgControlStatus, NG_VALIDATORS, RequiredValidator, ValidatorFn, Validators } from '@angular/forms';
import { DwComponent, DwComponentType, DwExpressionService, DwMetaDataFormApi, DwMetaDataService, DwMetaDataServiceToken, DwSectionBaseComponent, DwSecurityUserService, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken, DwMetaDataFormStateService, DwContainerOptions, isEmptyObject, DwLookupInfo, DwFormActionInterceptor, DwFormAction, DwActionPreference, DwFormActionConfig, DwFormActionKeys, DwFormActionInterceptResult, DwQueryHelper, DwQueryFieldsHelper } from '@devwareapps/devware-cap';
import { map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { values } from 'underscore';
import { EmployeeReviewCategories } from '../../meta-data/constants/employee-review-categories.enum';
import { EmployeeReviewQuestionSetupLevel } from '../../meta-data/constants/employee-review-question-setup-level.enum';
import { EmployeeReviewAnswerEntity, EmployeeReviewAnswerValueEntity, EmployeeReviewEntity, EmployeeReviewQuestionEntity, PerformanceManagementMetaDataItemNames, PerformanceManagementMetaDataService, EmployeeReviewQuestionSetupEntity, PerformanceManagementMetaDataLookups } from '../../meta-data/performancemanagement-meta-data.service';
import { EmployeeReviewQuestionData } from '../../models/employee-review-question-data.model';
import { PerformanceManagementRepositoryService } from '../../services/performance-management-repository.service';
import { Observable, of } from 'rxjs';

@DwComponent(
  {
    key: 'employee-review-question-setup',
    name: 'Employee Review Question Setup',
    componentType: DwComponentType.formSection,
    isGlobal: false,
    parentItemName: PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup,
  }
)
@Component({
  selector: 'app-employee-review-question-setup',
  templateUrl: './employee-review-question-setup.component.html',
  styleUrls: ['./employee-review-question-setup.component.scss']
})
export class EmployeeReviewQuestionSetupComponent extends DwSectionBaseComponent {
  relatedQuestionSetups: EmployeeReviewQuestionSetupEntity[];
  levelLookupItems: DwLookupInfo[];

  currentQuestionsDisplay: string = 'Additional';

  hideBiddingQuestion?: boolean = false;
  hideRevenueQuestion?: boolean = false;
  questionSetup: EmployeeReviewQuestionSetupEntity;

  constructor(private dwSecurityUserService: DwSecurityUserService,
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private perfMonRepo: PerformanceManagementRepositoryService,
    private dwMetaDataFormStateService: DwMetaDataFormStateService,
    private cdr: ChangeDetectorRef

  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);

    const md = new PerformanceManagementMetaDataService();

    this.dwMetaDataService.getLookup(PerformanceManagementMetaDataLookups.EmployeeReviewQuestionSetupLevelAllItems)
      .subscribe(levelLookupItems => {
        this.levelLookupItems = levelLookupItems;

        this.setupLevelValue();
      });
  }

  setupComponentComplete(): void {
    // setup questions once data is loaded
    this.runAfterFormDataLoaded(() => this.setupQuestions());

    this.dwMetaDataFormStateService.addActionInterceptor(this.getFormActionInterceptor())

    // Setup questions when the form group changes
    this.formGroup.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(value => this.setupQuestions());
  }

  getFormActionInterceptor(): DwFormActionInterceptor {
    // Create a form interceptor so we can latch onto the save and abort if needed
    const interceptor: DwFormActionInterceptor = {
      interceptorName: 'pm-question-setup-interceptor',
      onAfterAction: (actionPref: DwActionPreference<DwFormActionConfig>, actionConfig: any, actionResult: any) => {

        const result: DwFormActionInterceptResult = {
          abort: false
        }

        // Ensure this is after a form save
        if (actionPref.actionKey != DwFormActionKeys.formSave) {
          return of(result);
        }

        return this.updateTeamQuestions()
          .pipe(map(() => {
            return result;
          }));
      },
    }

    return interceptor;
  }

  updateTeamQuestions(): Observable<any> {
    if (this.questionSetup?.EmployeeReviewQuestionSetupLevelId != EmployeeReviewQuestionSetupLevel.department) {
      return of(null);
    }

    if (!this.questionSetup.HideBiddingQuestions && !this.questionSetup.HideRevenueQuestions) {
      return of(null);
    }

    return this.perfMonRepo.updateTeamBiddingAndRevenueQuestions(this.questionSetup.WachterDepartmentId, this.questionSetup.ReviewYear, this.questionSetup.HideRevenueQuestions, this.questionSetup.HideBiddingQuestions);
  }

  setupLevelValue() {
    this.questionSetup = this.dwMetaDataFormStateService.state.formApi.getFormData();

    if (!this.levelLookupItems || !this.questionSetup?.EmployeeReviewQuestionSetupLevelId) {
      return;
    }

    const levelLookup = this.levelLookupItems.find(i => i.Id == this.questionSetup.EmployeeReviewQuestionSetupLevelId.toString());

    this.currentQuestionsDisplay = levelLookup?.Display || 'Additional';
  }

  setupQuestions(): void {
    this.questionSetup = this.dwMetaDataFormStateService.state.formApi.getFormData();

    this.cdr.detectChanges();

    this.setupLevelValue();

    if (!this.questionSetup?.EmployeeReviewQuestionSetupLevelId || !this.questionSetup?.ReviewYear) {
      return;
    }

    if (this.questionSetup?.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.team && !this.questionSetup?.WachterDepartmentId) {
      return;
    }

    this.perfMonRepo.getQuestionSetupByLevel(this.questionSetup.EmployeeReviewQuestionSetupLevelId, this.questionSetup.ReviewYear, this.questionSetup.WachterDepartmentId)
      .subscribe(relatedQuestionSetups => {
        this.relatedQuestionSetups = relatedQuestionSetups;

        this.setupRevenueBiddingQuestionSettings();

      });
  }


  setRequired(attrName: string, isRequired: boolean) {

    // Meta data service return fields with the item name as a suffix. We need to parse it to get just the field name
    var qh = new DwQueryFieldsHelper();

    const controlName = qh.parseFullFieldName(attrName).FieldName;

    const control = this.formGroup.get(controlName);

    if (!control) {
      return;
    }

    if (isRequired) {
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
    }

  }

  setupRevenueBiddingQuestionSettings() {
    if (!this.relatedQuestionSetups) {
      return;
    }

    // Hide these for global
    if (this.questionSetup?.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.global) {
      this.hideBiddingQuestion = this.hideRevenueQuestion = true;
      return;
    }

    // Always show for department
    if (this.questionSetup?.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department) {
      this.hideBiddingQuestion = this.hideRevenueQuestion = false;
    }

    // Must be at the team level

    const deptSetup = this.relatedQuestionSetups.find(qs => qs.EmployeeReviewQuestionSetupLevelId == EmployeeReviewQuestionSetupLevel.department);

    // If no department setup then, then show the questions
    if (!deptSetup) {
      this.hideBiddingQuestion = this.hideRevenueQuestion = false;
      return;
    }

    this.hideBiddingQuestion = deptSetup.HideBiddingQuestions;
    this.hideRevenueQuestion = deptSetup.HideRevenueQuestions;
  }

}
